import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Eye, Pencil, Globe, GlobeLock, Trash2 } from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
import { api } from '../../../lib/api';
import PlanBadge from '../../superadmin/components/PlanBadge';

const PAGE_SIZE = 10;

/**
 * @param {{ weddings: Array, onRefresh: () => void }} props
 */
const WeddingTable = ({ weddings, loading, onRefresh }) => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const [actionLoading, setActionLoading] = useState({});

  // Client-side filtering
  const filtered = useMemo(() => {
    let result = weddings || [];

    if (search) {
      const q = search.toLowerCase();
      result = result.filter((w) => {
        const name = getCoupleName(w).toLowerCase();
        const slug = (w.slug || '').toLowerCase();
        return name.includes(q) || slug.includes(q);
      });
    }

    if (statusFilter) {
      result = result.filter((w) => w.status === statusFilter);
    }

    return result;
  }, [weddings, search, statusFilter]);

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const setActionBusy = (id, busy) => setActionLoading((prev) => ({ ...prev, [id]: busy }));

  const handleTogglePublish = async (wedding) => {
    setActionBusy(wedding.id, true);
    try {
      if (wedding.status === 'published') {
        await api.unpublishWedding(token, wedding.id);
      } else {
        await api.publishWedding(token, wedding.id);
      }
      onRefresh();
    } catch (err) {
      console.error('Toggle publish failed:', err);
    } finally {
      setActionBusy(wedding.id, false);
    }
  };

  const handleDelete = async (wedding) => {
    if (!confirm('Are you sure you want to delete this draft wedding? This cannot be undone.')) return;
    setActionBusy(wedding.id, true);
    try {
      await api.deleteWedding(token, wedding.id);
      onRefresh();
    } catch (err) {
      console.error('Delete failed:', err);
    } finally {
      setActionBusy(wedding.id, false);
    }
  };

  const handleSearch = (e) => {
    setSearch(e.target.value);
    setPage(1);
  };

  return (
    <section
      className="rounded-sm overflow-hidden bg-white"
      style={{ border: '1px solid rgba(212,175,55,0.4)', boxShadow: '0 4px 20px rgba(184,134,11,0.08)' }}
    >
      {/* Toolbar */}
      <div className="p-5 border-b border-[#E6D8AD]/50 bg-[#FDFCF5] flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="relative w-full max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#54626F]/50" />
          <input
            type="text"
            value={search}
            onChange={handleSearch}
            placeholder="Search by couple name or slug..."
            className="w-full pl-10 pr-4 py-2 bg-white border border-[#E6D8AD] rounded text-sm text-[#36454F] focus:ring-1 focus:ring-[#D4AF37] focus:border-[#D4AF37] placeholder:text-[#54626F]/40"
            style={{ fontFamily: "'Lato', sans-serif" }}
          />
        </div>
        <div className="flex items-center gap-3">
          <select
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
            className="px-3 py-2 bg-white border border-[#E6D8AD] rounded text-xs uppercase tracking-wide text-[#36454F] font-bold focus:ring-[#D4AF37] focus:border-[#D4AF37]"
          >
            <option value="">All Status</option>
            <option value="published">Published</option>
            <option value="draft">Draft</option>
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full text-left border-collapse">
          <thead>
            <tr className="bg-[#FAF9F0] border-b border-[#E6D8AD] text-xs uppercase tracking-widest text-[#54626F] font-bold">
              <th className="px-6 py-4" style={{ fontFamily: "'Cinzel', serif" }}>Couple Name</th>
              <th className="px-6 py-4" style={{ fontFamily: "'Cinzel', serif" }}>Plan</th>
              <th className="px-6 py-4 text-center" style={{ fontFamily: "'Cinzel', serif" }}>RSVPs</th>
              <th className="px-6 py-4" style={{ fontFamily: "'Cinzel', serif" }}>Status</th>
              <th className="px-6 py-4 text-right" style={{ fontFamily: "'Cinzel', serif" }}>Action</th>
            </tr>
          </thead>
          <tbody className="text-sm text-[#36454F] divide-y divide-[#F3E5AB]/50 bg-[#FFFFF0]" style={{ fontFamily: "'Lato', sans-serif" }}>
            {loading ? (
              <tr>
                <td colSpan={5} className="px-6 py-12 text-center text-[#54626F]">
                  <div className="w-6 h-6 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin mx-auto" />
                </td>
              </tr>
            ) : paginated.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-6 py-12 text-center text-[#54626F]">No weddings found</td>
              </tr>
            ) : (
              paginated.map((w) => {
                const coupleName = getCoupleName(w);
                const initials = getInitials(coupleName);
                const statusStyle = getStatusStyle(w.status);
                const busy = actionLoading[w.id];

                return (
                  <tr key={w.id} className="hover:bg-[#FFFDF5] transition-colors group">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-[#E6D8AD] flex items-center justify-center text-[10px] font-bold text-[#8B6508]">
                          {initials}
                        </div>
                        <div>
                          <div className="font-bold text-[#36454F]">{coupleName}</div>
                          <div className="text-xs text-[#54626F]">/w/{w.slug}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <PlanBadge plan={w.plan_id || 'free'} />
                    </td>
                    <td className="px-6 py-4 text-center">{w.rsvp_count || 0}</td>
                    <td className="px-6 py-4">
                      <span className={`flex items-center gap-1.5 text-xs font-bold ${statusStyle.text}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${statusStyle.dot}`} />
                        {statusStyle.label}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-2 opacity-60 group-hover:opacity-100 transition-opacity">
                        {w.slug && w.status === 'published' && (
                          <button
                            onClick={() => window.open(`/w/${w.slug}`, '_blank')}
                            className="p-1.5 hover:bg-[#F3E5AB] rounded text-[#B8860B] transition-colors"
                            title="Preview Live Site"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                        )}
                        <button
                          onClick={() => navigate(`/dashboard/weddings/${w.id}`)}
                          className="p-1.5 hover:bg-[#F3E5AB] rounded text-[#B8860B] transition-colors"
                          title="Edit"
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleTogglePublish(w)}
                          disabled={busy}
                          className={`p-1.5 rounded transition-colors ${
                            w.status === 'published'
                              ? 'hover:bg-amber-50 text-amber-500 hover:text-amber-700'
                              : 'hover:bg-green-50 text-green-500 hover:text-green-700'
                          } disabled:opacity-40`}
                          title={w.status === 'published' ? 'Unpublish' : 'Publish'}
                        >
                          {w.status === 'published' ? <GlobeLock className="w-4 h-4" /> : <Globe className="w-4 h-4" />}
                        </button>
                        {w.status === 'draft' && (
                          <button
                            onClick={() => handleDelete(w)}
                            disabled={busy}
                            className="p-1.5 hover:bg-red-50 rounded text-red-400 hover:text-red-600 transition-colors disabled:opacity-40"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="p-4 border-t border-[#E6D8AD]/50 bg-[#FDFCF5] flex items-center justify-between">
        <span className="text-xs text-[#54626F] uppercase tracking-wider" style={{ fontFamily: "'Lato', sans-serif" }}>
          Showing {filtered.length === 0 ? 0 : (page - 1) * PAGE_SIZE + 1}-{Math.min(page * PAGE_SIZE, filtered.length)} of {filtered.length}
        </span>
        <div className="flex gap-2">
          <button
            onClick={() => setPage(Math.max(1, page - 1))}
            disabled={page === 1}
            className="px-3 py-1.5 border border-[#E6D8AD] rounded text-xs uppercase font-bold text-[#54626F] hover:text-[#36454F] hover:bg-white disabled:opacity-50"
          >
            Prev
          </button>
          {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
            const p = i + 1;
            const isActive = p === page;
            return (
              <button
                key={p}
                onClick={() => setPage(p)}
                className={`px-3 py-1.5 border rounded text-xs uppercase font-bold ${
                  isActive
                    ? 'border-[#E6D8AD] text-white bg-[#C5A059] hover:bg-[#B8860B]'
                    : 'border-[#E6D8AD] text-[#54626F] hover:text-[#36454F] hover:bg-white'
                }`}
              >
                {p}
              </button>
            );
          })}
          {totalPages > 5 && <span className="px-2 py-1.5 text-[#54626F]">...</span>}
          <button
            onClick={() => setPage(Math.min(totalPages, page + 1))}
            disabled={page >= totalPages}
            className="px-3 py-1.5 border border-[#E6D8AD] rounded text-xs uppercase font-bold text-[#54626F] hover:text-[#36454F] hover:bg-white disabled:opacity-50"
          >
            Next
          </button>
        </div>
      </div>
    </section>
  );
};

// ── Helpers ─────────────────────────────────────────────────────────────────

function getCoupleName(wedding) {
  try {
    const content = typeof wedding.content === 'string' ? JSON.parse(wedding.content) : wedding.content;
    return content?.couple?.names || wedding.slug || 'Unnamed';
  } catch {
    return wedding.slug || 'Unnamed';
  }
}

function getInitials(name) {
  if (!name) return '??';
  return name.split(/[\s&]+/).map((w) => w[0]?.toUpperCase() || '').join('').slice(0, 2);
}

function getStatusStyle(status) {
  switch (status) {
    case 'published':
      return { dot: 'bg-green-500', text: 'text-green-700', label: 'Active' };
    case 'draft':
      return { dot: 'bg-amber-500', text: 'text-amber-600', label: 'Draft' };
    default:
      return { dot: 'bg-gray-400', text: 'text-gray-500', label: status || 'Unknown' };
  }
}

export default WeddingTable;
